// Your portrait — the web half of the Self-Model. Reads and writes the SAME
// documents iOS does (users/{uid}/selfModel/graph, its claims and
// confirmations, preferences/selfModel, appConfig/self_model_v1) and the same
// endpoints (rebuildSelfModel, deleteSelfModel), so both apps see one truth.
//
// Doctrine carried over from iOS: the person's word outranks everything;
// nothing counts as true until they've seen it; decisions are append-only
// confirmation records the server applies; every failure is said out loud.

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import { state } from "./state";
import { render as _render } from "./ui";
import { showToast } from "./utils";
import type {
  PortraitClaim,
  PortraitCluster,
  PortraitPresentation,
  PortraitState,
} from "./types";

const REBUILD_URL = "https://us-central1-language-of-thoughts.cloudfunctions.net/rebuildSelfModel";
const ERASE_URL = "https://us-central1-language-of-thoughts.cloudfunctions.net/deleteSelfModel";

export const EMPTY_PRESENTATION: PortraitPresentation = {
  clusters: [],
  needsYourWordIds: [],
  display: {},
  carryQuestion: null,
  portraitText: null,
};

export function initialPortraitState(): PortraitState {
  return {
    available: false,
    availabilityResolved: false,
    enabled: false,
    consentResolved: false,
    unreviewedOptIn: false,
    loading: false,
    loadFailed: false,
    enabling: false,
    claims: [],
    presentation: EMPTY_PRESENTATION,
    decidedIds: new Set(),
    deferredIds: new Set(),
    lastDecidedId: null,
    selectedTheme: null,
    focusedNodeId: null,
    selectedClaimId: null,
    claimText: "",
    claimTextPurpose: null,
    notQuiteClaimId: null,
    mode: "home",
    reviewFilter: "all",
    reviewSearch: "",
    pendingClaimIds: new Set(),
  };
}

export function resetPortraitState(): void {
  state.portrait = initialPortraitState();
}

// ── Rollout (mirrors SelfModelService.resolveStage) ─────────────────────────

async function rolloutBucket(uid: string): Promise<number> {
  const digest = await crypto.subtle.digest("SHA-1", new TextEncoder().encode(uid));
  const bytes = new Uint8Array(digest);
  return ((bytes[0] << 8) | bytes[1]) % 100;
}

async function resolveStage(
  data: Record<string, unknown>,
  stageKey: string,
  percentKey: string,
  uid: string,
): Promise<boolean> {
  if (data.killSwitch === true) return false;
  const allowed = Array.isArray(data.allowedUids) ? (data.allowedUids as string[]) : [];
  const cohort = Array.isArray(data.cohortUids) ? (data.cohortUids as string[]) : [];
  switch (String(data[stageKey] || "off")) {
    case "all": return true;
    case "internal": return allowed.includes(uid);
    case "cohort": return cohort.includes(uid) || allowed.includes(uid);
    case "percentage": {
      const percent = Number(data[percentKey] || 0);
      if (allowed.includes(uid)) return true;
      return (await rolloutBucket(uid)) < Math.max(0, Math.min(100, percent));
    }
    default: return false;
  }
}

export async function refreshPortraitAvailability(): Promise<void> {
  const uid = state.user?.uid;
  if (!uid) {
    state.portrait.available = false;
    state.portrait.availabilityResolved = true;
    return;
  }
  try {
    const snap = await getDoc(doc(db, "appConfig", "self_model_v1"));
    const data = (snap.exists() ? snap.data() : {}) as Record<string, unknown>;
    state.portrait.available = await resolveStage(data, "archiveRollout", "archivePercent", uid);
  } catch {
    state.portrait.available = false;
  }
  state.portrait.availabilityResolved = true;
}

export async function refreshPortraitConsent(): Promise<void> {
  const uid = state.user?.uid;
  if (!uid) return;
  try {
    const snap = await getDoc(doc(db, "users", uid, "preferences", "selfModel"));
    const data = (snap.exists() ? snap.data() : {}) as Record<string, unknown>;
    state.portrait.enabled = data.enabled === true;
    state.portrait.unreviewedOptIn = data.companionUnreviewedOptIn === true;
  } catch {
    // Leave the last known answer; the intro/home decision waits on it.
  }
  state.portrait.consentResolved = true;
}

// ── Loading the graph ───────────────────────────────────────────────────────

function num(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function refs(raw: unknown): PortraitClaim["evidence"] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => {
    const r = (item || {}) as Record<string, unknown>;
    return {
      sourceType: String(r.sourceType || ""),
      sourceId: String(r.sourceId || ""),
      weight: num(r.weight, 1),
      at: num(r.at, 0),
    };
  }).filter((r) => r.sourceId);
}

function parseClaim(id: string, raw: Record<string, unknown>): PortraitClaim | null {
  if (!raw || typeof raw.statement !== "string") return null;
  const confirmation = (raw.confirmation || {}) as Record<string, unknown>;
  const stability = (raw.stability || {}) as Record<string, unknown>;
  const temporal = (raw.temporal || {}) as Record<string, unknown>;
  const confidence = (raw.confidence || {}) as Record<string, unknown>;
  const provenance = (raw.provenance || {}) as Record<string, unknown>;
  const scopeRaw = raw.scope as Record<string, unknown> | undefined;
  return {
    id: String(raw.id || id),
    lineageId: String(raw.lineageId || ""),
    kind: String(raw.kind || ""),
    subjectKey: String(raw.subjectKey || ""),
    statement: String(raw.statement || ""),
    userQualifier: raw.userQualifier ? String(raw.userQualifier) : null,
    hiddenFromSurfaces: raw.hiddenFromSurfaces === true,
    scope: scopeRaw
      ? { context: String(scopeRaw.context || ""), conditions: Array.isArray(scopeRaw.conditions) ? (scopeRaw.conditions as string[]).map(String) : [] }
      : null,
    evidence: refs(raw.evidence),
    counterEvidence: refs(raw.counterEvidence),
    confidence: { abstained: confidence.abstained === true, band: String(confidence.band || "") },
    stability: {
      state: String(stability.state || "episodic"),
      distinctDayCount: num(stability.distinctDayCount),
      spanDays: num(stability.spanDays),
    },
    confirmation: {
      status: String(confirmation.status || "unconfirmed"),
      method: confirmation.method ? String(confirmation.method) : null,
      at: confirmation.at == null ? null : num(confirmation.at),
    },
    state: String(raw.state || "active"),
    temporal: { firstEvidenceAt: num(temporal.firstEvidenceAt), lastEvidenceAt: num(temporal.lastEvidenceAt) },
    links: Array.isArray(raw.links)
      ? (raw.links as Record<string, unknown>[]).map((l) => ({
        type: String(l.type || ""),
        claimId: String(l.claimId || ""),
      })).filter((l) => l.claimId)
      : [],
    provenance: { generator: String(provenance.generator || "") },
    history: Array.isArray(raw.history)
      ? (raw.history as Record<string, unknown>[]).map((h) => ({ event: String(h.event || ""), at: num(h.at), byUser: h.byUser === true }))
      : [],
  };
}

function parsePresentation(raw: unknown, byId: Map<string, PortraitClaim>): PortraitPresentation {
  const document = (raw || {}) as Record<string, unknown>;
  if (document.schemaVersion !== "self-model-presentation-v1") return EMPTY_PRESENTATION;
  const clusters: PortraitCluster[] = (Array.isArray(document.clusters) ? (document.clusters as Record<string, unknown>[]) : [])
    .map((c, index) => {
      const groups = Array.isArray(c.groups) ? (c.groups as Record<string, unknown>[]) : [];
      const representativeIds = groups.map((g) => String(g.representativeId || "")).filter((id) => byId.has(id));
      const similarIds = groups.flatMap((g) => (Array.isArray(g.similarIds) ? (g.similarIds as string[]) : [])).map(String).filter((id) => byId.has(id));
      return {
        id: `${index}:${String(c.title || "")}`,
        title: String(c.title || ""),
        synthesis: String(c.synthesis || "").replace(/\.{2,}\s*$/, "."),
        representativeIds,
        similarIds,
        temporalLabel: String(c.temporalLabel || "Recent"),
        confirmedCount: num(c.confirmedCount),
        needsWordCount: num(c.needsWordCount),
      };
    })
    .filter((c) => c.title && c.representativeIds.length > 0);
  const portrait = (document.portrait || {}) as Record<string, unknown>;
  return {
    clusters,
    needsYourWordIds: (Array.isArray(document.needsYourWordIds) ? (document.needsYourWordIds as string[]) : []).map(String).filter((id) => byId.has(id)),
    display: (document.display && typeof document.display === "object") ? (document.display as Record<string, string>) : {},
    carryQuestion: document.carryQuestion ? String(document.carryQuestion) : null,
    portraitText: portrait.text ? String(portrait.text) : null,
  };
}

export async function loadPortrait(): Promise<void> {
  const uid = state.user?.uid;
  if (!uid) return;
  state.portrait.loading = state.portrait.claims.length === 0;
  state.portrait.loadFailed = false;
  _render();
  try {
    const graphRef = doc(db, "users", uid, "selfModel", "graph");
    const [graphSnap, claimsSnap] = await Promise.all([
      getDoc(graphRef),
      getDocs(collection(graphRef, "claims")),
    ]);
    const claims = claimsSnap.docs
      .map((d) => parseClaim(d.id, d.data() as Record<string, unknown>))
      .filter((c): c is PortraitClaim => !!c)
      .sort((a, b) => b.temporal.lastEvidenceAt - a.temporal.lastEvidenceAt);
    const byId = new Map(claims.map((c) => [c.id, c]));
    state.portrait.claims = claims;
    state.portrait.presentation = parsePresentation((graphSnap.exists() ? graphSnap.data() : {})?.presentation, byId);
  } catch {
    state.portrait.loadFailed = true;
  }
  state.portrait.loading = false;
  _render();
}

// ── Predicates and labels (mirror SelfModelClaim extensions) ───────────────

export function isUserOwned(claim: PortraitClaim): boolean {
  return claim.confirmation.status === "confirmed" || claim.confirmation.status === "corrected" || claim.provenance.generator === "user_authored";
}

export function awaitsWord(claim: PortraitClaim): boolean {
  return claim.state === "active" && claim.confirmation.status === "unconfirmed" && !isUserOwned(claim);
}

export function isKept(claim: PortraitClaim): boolean {
  return !claim.hiddenFromSurfaces && claim.state === "active"
    && ["confirmed", "corrected", "qualified"].includes(claim.confirmation.status);
}

export function isHistorical(claim: PortraitClaim): boolean {
  return claim.state === "superseded" || claim.state === "outdated";
}

export type ArchiveGroup = "confirmed" | "suggested" | "needsClarification" | "conflicted" | "changedOverTime" | "rejected" | "historical" | "hidden";

export function archiveGroup(claim: PortraitClaim): ArchiveGroup {
  if (claim.hiddenFromSurfaces) return "hidden";
  switch (claim.state) {
    case "superseded": return "historical";
    case "rejected": return "rejected";
    case "outdated": return "changedOverTime";
    case "disputed": return "conflicted";
    case "candidate": return "needsClarification";
    default:
      if (claim.scope?.context === "contextual") return "conflicted";
      switch (claim.confirmation.status) {
        case "confirmed":
        case "corrected": return "confirmed";
        case "qualified": return "needsClarification";
        default: return "suggested";
      }
  }
}

export const ARCHIVE_GROUP_TITLES: Record<ArchiveGroup, string> = {
  confirmed: "Confirmed by you",
  suggested: "Noticed by LOT",
  needsClarification: "Needs your clarification",
  conflicted: "Conflicted or context-dependent",
  changedOverTime: "Changed over time",
  rejected: "You said this isn't true",
  historical: "Earlier versions",
  hidden: "Hidden from reflections",
};

export function compactLabel(claim: PortraitClaim): string {
  switch (claim.state) {
    case "superseded": return "Earlier version";
    case "outdated": return "Changed";
    case "rejected": return "Not true, you said";
    case "disputed": return "Conflicted";
    default: break;
  }
  if (claim.confirmation.status === "confirmed" || claim.confirmation.status === "corrected") return "Confirmed by you";
  if (claim.confirmation.status === "unconfirmed" && !isUserOwned(claim)) return "Is this true?";
  switch (claim.stability.state) {
    case "stable": return "Long-running";
    case "recurring": return "Returning";
    case "fading":
    case "dormant": return "Quieter lately";
    default: return "Recent";
  }
}

export function stabilityDescription(claim: PortraitClaim): string {
  switch (claim.stability.state) {
    case "episodic": return "Has come up once";
    case "emerging": return "Starting to come up";
    case "recurring": return "Comes up regularly";
    case "stable": return "Consistent over months";
    case "fading": return "Less present lately";
    case "dormant": return "Quiet for a while";
    default: return "";
  }
}

export function provenanceLabel(claim: PortraitClaim): string {
  if (isUserOwned(claim)) return "Written by you";
  if (claim.confirmation.status === "qualified") return "Noticed by LOT · you said it's partly true";
  return "Noticed by LOT";
}

export function evidenceSummary(claim: PortraitClaim, availableCount: number): string {
  if (availableCount <= 0) return "Its supporting thoughts are no longer available";
  const fmt = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  const countText = availableCount === 1 ? "1 thought" : `${availableCount} thoughts`;
  const first = claim.temporal.firstEvidenceAt;
  const last = claim.temporal.lastEvidenceAt;
  if (first <= 0 || fmt(first) === fmt(last)) return `Based on ${countText} from ${fmt(last)}`;
  return `Based on ${countText} between ${fmt(first)} and ${fmt(last)}`;
}

/** A generated observation is a hypothesis until accepted — second person
 * (server-composed) before the person's word, their own words after. */
export function observationText(claim: PortraitClaim): string {
  if (!awaitsWord(claim)) return claim.statement;
  return state.portrait.presentation.display[claim.id] || claim.statement;
}

export function lastMentionedLine(claim: PortraitClaim): string {
  const date = new Date(claim.temporal.lastEvidenceAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  return `Last mentioned: ${date}`;
}

export function claimById(id: string | null): PortraitClaim | null {
  if (!id) return null;
  return state.portrait.claims.find((c) => c.id === id) || null;
}

/** The first of the person's own thoughts behind a claim, from the notes
 * already loaded on this device. Local journal only; nothing generated. */
export function firstExcerpt(claim: PortraitClaim): string | null {
  const notesById = new Map(state.notes.map((n) => [n.id, n]));
  const entry = claim.evidence
    .filter((e) => e.sourceType === "entry")
    .sort((a, b) => b.at - a.at)
    .map((e) => notesById.get(e.sourceId))
    .find((n) => n && !n.isEncrypted && n.text.trim());
  if (!entry) return null;
  const text = entry.text.trim();
  return text.length > 140 ? `${text.slice(0, 137)}…` : text;
}

export function themeOfClaim(): Map<string, string> {
  const out = new Map<string, string>();
  state.portrait.presentation.clusters.forEach((cluster) => {
    [...cluster.representativeIds, ...cluster.similarIds].forEach((id) => out.set(id, cluster.title));
  });
  return out;
}

// ── Deck and remembered cards (set-based bookkeeping, like iOS) ────────────

export function deckClaims(): PortraitClaim[] {
  const p = state.portrait;
  const byId = new Map(p.claims.map((c) => [c.id, c]));
  const seen = new Set<string>();
  const out: PortraitClaim[] = [];
  p.presentation.needsYourWordIds.forEach((id) => {
    const claim = byId.get(id);
    if (claim && !seen.has(id)) { seen.add(id); out.push(claim); }
  });
  p.claims
    .filter((c) => awaitsWord(c) && !c.hiddenFromSurfaces)
    .sort((a, b) => b.temporal.lastEvidenceAt - a.temporal.lastEvidenceAt)
    .forEach((c) => { if (!seen.has(c.id)) { seen.add(c.id); out.push(c); } });
  return out.filter((c) => !p.decidedIds.has(c.id) && !p.deferredIds.has(c.id));
}

export interface RememberedCard { claim: PortraitClaim; theme: string | null }

export function rememberedCards(): RememberedCard[] {
  const p = state.portrait;
  const byId = new Map(p.claims.map((c) => [c.id, c]));
  const seen = new Set<string>();
  const out: RememberedCard[] = [];
  p.presentation.clusters.forEach((cluster) => {
    [...cluster.representativeIds, ...cluster.similarIds].forEach((id) => {
      const claim = byId.get(id);
      if (claim && isKept(claim) && !seen.has(id)) { seen.add(id); out.push({ claim, theme: cluster.title }); }
    });
  });
  p.claims.filter(isKept)
    .sort((a, b) => b.temporal.lastEvidenceAt - a.temporal.lastEvidenceAt)
    .forEach((claim) => { if (!seen.has(claim.id)) { seen.add(claim.id); out.push({ claim, theme: null }); } });
  return out;
}

export function keptCount(): number {
  return state.portrait.claims.filter(isKept).length;
}

const REFLECTIVE_QUESTIONS = [
  "What mattered to you this week, even if it was small?",
  "When did you last feel most like yourself?",
  "What's something you keep choosing, again and again?",
];

/** The next thing worth writing about, from the portrait's own structure. */
export function growPrompt(): string {
  const p = state.portrait.presentation;
  if (p.carryQuestion) return p.carryQuestion;
  if (p.clusters.length >= 2) {
    const thin = [...p.clusters].sort((a, b) => a.confirmedCount - b.confirmedCount)[0];
    return `What else belongs under “${thin.title}”?`;
  }
  return REFLECTIVE_QUESTIONS[new Date().getDate() % REFLECTIVE_QUESTIONS.length];
}

export function reflectiveQuestions(): string[] {
  return REFLECTIVE_QUESTIONS;
}

// ── Writes ─────────────────────────────────────────────────────────────────

export type ClaimAction = "confirm" | "qualify" | "contextualize" | "add_context" | "correct" | "reject" | "mark_outdated" | "revise" | "hide" | "unhide" | "delete";

export async function submitPortraitConfirmation(claimId: string, action: ClaimAction, statement?: string | null): Promise<void> {
  const uid = state.user?.uid;
  if (!uid) throw new Error("not_signed_in");
  const data: Record<string, unknown> = {
    schemaVersion: "claim-confirmation-v1",
    claimId,
    action,
    method: "explicit_review",
    applied: false,
    recordedAt: Date.now(),
  };
  if (statement && statement.trim()) data.statement = statement.trim();
  await addDoc(collection(doc(db, "users", uid, "selfModel", "graph"), "confirmations"), data);
}

const DID_NOT_SAVE = "That didn't save. Check your connection and try again.";

/** A deck decision: the card leaves at once, the server applies it; a failure
 * brings the card back and says so. */
export async function decidePortrait(claimId: string, action: ClaimAction): Promise<void> {
  const p = state.portrait;
  p.lastDecidedId = claimId;
  p.decidedIds.add(claimId);
  p.notQuiteClaimId = null;
  _render();
  try {
    await submitPortraitConfirmation(claimId, action);
  } catch {
    p.lastDecidedId = null;
    p.decidedIds.delete(claimId);
    showToast(DID_NOT_SAVE, "error");
    _render();
  }
}

export async function undoPortraitDecision(): Promise<void> {
  const p = state.portrait;
  const id = p.lastDecidedId;
  if (!id) return;
  p.lastDecidedId = null;
  _render();
  try {
    await submitPortraitConfirmation(id, "revise");
    p.decidedIds.delete(id);
  } catch {
    p.lastDecidedId = id;
    showToast(DID_NOT_SAVE, "error");
  }
  _render();
}

/** A governance action from the detail view: the server applies it; the
 * archive refreshes after a beat so the durable state shows. */
export async function performPortraitAction(claimId: string, action: ClaimAction, statement?: string | null): Promise<void> {
  const p = state.portrait;
  try {
    await submitPortraitConfirmation(claimId, action, statement);
    p.pendingClaimIds.add(claimId);
    p.selectedClaimId = null;
    p.claimText = "";
    p.claimTextPurpose = null;
    _render();
    await new Promise((resolve) => window.setTimeout(resolve, 2000));
    await loadPortrait();
    p.pendingClaimIds.delete(claimId);
    _render();
  } catch {
    showToast(DID_NOT_SAVE, "error");
  }
}

export async function setPortraitEnabled(enabled: boolean): Promise<void> {
  const uid = state.user?.uid;
  if (!uid) throw new Error("not_signed_in");
  await setDoc(doc(db, "users", uid, "preferences", "selfModel"), { enabled, updatedAt: Timestamp.now() }, { merge: true });
  state.portrait.enabled = enabled;
}

export async function setPortraitUnreviewedOptIn(enabled: boolean): Promise<void> {
  const uid = state.user?.uid;
  if (!uid) throw new Error("not_signed_in");
  await setDoc(doc(db, "users", uid, "preferences", "selfModel"), { companionUnreviewedOptIn: enabled }, { merge: true });
  state.portrait.unreviewedOptIn = enabled;
}

async function bearerPost(url: string, body: Record<string, unknown> = {}): Promise<Response> {
  const user = auth.currentUser;
  if (!user) throw new Error("not_signed_in");
  const token = await user.getIdToken();
  return fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function requestPortraitRebuild(): Promise<void> {
  if (!state.portrait.enabled) return;
  const response = await bearerPost(REBUILD_URL, {});
  if (!response.ok) throw new Error(`rebuild_${response.status}`);
}

export async function enablePortraitAndLoad(): Promise<void> {
  const p = state.portrait;
  p.enabling = true;
  _render();
  try {
    await setPortraitEnabled(true);
    await requestPortraitRebuild().catch(() => undefined);
    await loadPortrait();
  } catch {
    p.loadFailed = true;
  }
  p.enabling = false;
  _render();
}

/** Off means Companion only uses what you've confirmed; the graph stays. */
export async function stopPortraitNoticing(): Promise<void> {
  try {
    await setPortraitEnabled(false);
    state.portrait.claims = [];
    state.portrait.presentation = EMPTY_PRESENTATION;
    state.portrait.mode = "home";
  } catch {
    showToast("LOT couldn't turn this off. Check your connection and try again.", "error");
  }
  _render();
}

/** Erases every observation and decision. The person's thoughts are never
 * touched. Only a confirmed server response clears anything locally. */
export async function erasePortrait(): Promise<void> {
  try {
    const response = await bearerPost(ERASE_URL, {});
    if (!response.ok) throw new Error(`erase_${response.status}`);
    state.portrait.claims = [];
    state.portrait.presentation = EMPTY_PRESENTATION;
    state.portrait.enabled = false;
    state.portrait.mode = "home";
  } catch {
    showToast("Nothing was erased. Check your connection and try again.", "error");
  }
  _render();
}

// ── The map (port of PortraitConstellationModel; pure) ─────────────────────

export interface MapNode { id: string; label: string; theme: string | null; weight: number; kept: boolean; stable: boolean; quiet: boolean }
export interface MapEdge { a: string; b: string; kind: "link" | "sharedThought" | "tension" }
export interface MapModel { nodes: MapNode[]; edges: MapEdge[]; themes: string[]; thoughtCount: number }
export interface Point { x: number; y: number }

const MAX_EDGES = 40;
const EDGES_PER_NODE = 2;

function edgePriority(e: MapEdge): number {
  return e.kind === "tension" ? 3 : e.kind === "link" ? 2 : 1;
}

function pruneEdges(edges: MapEdge[], nodes: MapNode[]): MapEdge[] {
  const themeOf = new Map(nodes.map((n) => [n.id, n.theme]));
  const score = (e: MapEdge) => edgePriority(e) * 2 + (themeOf.get(e.a) != null && themeOf.get(e.a) === themeOf.get(e.b) ? 1 : 0);
  const kept: MapEdge[] = [];
  const keys = new Set<string>();
  nodes.forEach((node) => {
    edges.filter((e) => e.a === node.id || e.b === node.id)
      .sort((l, r) => (score(r) - score(l)) || ((l.a + l.b) < (r.a + r.b) ? -1 : 1))
      .slice(0, EDGES_PER_NODE)
      .forEach((e) => {
        const k = e.a < e.b ? `${e.a}|${e.b}` : `${e.b}|${e.a}`;
        if (!keys.has(k)) { keys.add(k); kept.push(e); }
      });
  });
  const cap = Math.min(MAX_EDGES, Math.max(nodes.length, Math.ceil(nodes.length * 1.5)));
  return kept.sort((l, r) => score(r) - score(l)).slice(0, cap);
}

export function buildPortraitMap(): MapModel {
  const p = state.portrait;
  const themeOf = themeOfClaim();
  const kept = p.claims.filter(isKept);
  const waiting = p.claims.filter((c) => awaitsWord(c) && !c.hiddenFromSurfaces && !p.decidedIds.has(c.id));
  const seed = (c: PortraitClaim, isKeptNode: boolean): MapNode => ({
    id: c.id,
    label: c.statement,
    theme: themeOf.get(c.id) || null,
    weight: c.evidence.filter((e) => e.sourceType === "entry").length,
    kept: isKeptNode,
    stable: c.stability.state === "stable" || c.stability.state === "recurring",
    quiet: c.stability.state === "fading" || c.stability.state === "dormant",
  });
  const nodes = [...kept.map((c) => seed(c, true)), ...waiting.map((c) => seed(c, false))];
  const ids = new Set(nodes.map((n) => n.id));
  const seen = new Set<string>();
  const key = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);
  const edges: MapEdge[] = [];
  [...kept, ...waiting].forEach((c) => {
    c.links.forEach((l) => {
      if (!ids.has(l.claimId) || l.claimId === c.id) return;
      const k = key(c.id, l.claimId);
      if (seen.has(k)) return;
      seen.add(k);
      edges.push({ a: c.id, b: l.claimId, kind: l.type === "contradicts" ? "tension" : "link" });
    });
  });
  const byThought = new Map<string, string[]>();
  [...kept, ...waiting].forEach((c) => {
    c.evidence.filter((e) => e.sourceType === "entry").forEach((e) => {
      byThought.set(e.sourceId, [...(byThought.get(e.sourceId) || []), c.id]);
    });
  });
  [...byThought.keys()].sort().forEach((thought) => {
    const members = [...new Set(byThought.get(thought) || [])].sort();
    for (let i = 0; i < members.length; i += 1) {
      for (let j = i + 1; j < members.length; j += 1) {
        const k = key(members[i], members[j]);
        if (seen.has(k)) continue;
        seen.add(k);
        edges.push({ a: members[i], b: members[j], kind: "sharedThought" });
      }
    }
  });
  const themes: string[] = [];
  nodes.forEach((n) => { if (n.theme && !themes.includes(n.theme)) themes.push(n.theme); });
  const thoughtCount = new Set(kept.flatMap((c) => c.evidence.filter((e) => e.sourceType === "entry").map((e) => e.sourceId))).size;
  return { nodes, edges: pruneEdges(edges, nodes), themes, thoughtCount };
}

export function mapPhase(id: string): number {
  let hash = 2166136261;
  for (const byte of new TextEncoder().encode(id)) {
    hash = Math.imul(hash ^ byte, 16777619) >>> 0;
  }
  return ((hash % 1000) / 1000) * 0.9;
}

function clamp(p: Point, w: number, h: number, margin: number): Point {
  return { x: Math.min(Math.max(p.x, margin), w - margin), y: Math.min(Math.max(p.y, margin), h - margin) };
}

export function mapThemeCenters(model: MapModel, w: number, h: number): Map<string, Point> {
  const out = new Map<string, Point>();
  const center = { x: w / 2, y: h / 2 };
  const base = Math.min(w, h);
  if (model.themes.length === 1) { out.set(model.themes[0], center); return out; }
  const radius = base * 0.33;
  model.themes.forEach((theme, k) => {
    const angle = -Math.PI / 2 + (k / model.themes.length) * 2 * Math.PI;
    out.set(theme, { x: center.x + Math.cos(angle) * radius * 1.35, y: center.y + Math.sin(angle) * radius * 0.78 });
  });
  return out;
}

export function mapLayout(model: MapModel, w: number, h: number): Map<string, Point> {
  const points = new Map<string, Point>();
  if (!model.nodes.length || w <= 0 || h <= 0) return points;
  const center = { x: w / 2, y: h / 2 };
  const base = Math.min(w, h);
  const centers = mapThemeCenters(model, w, h);
  const grouped = new Map<string, MapNode[]>();
  const unthemed: MapNode[] = [];
  model.nodes.forEach((n) => {
    if (n.theme) grouped.set(n.theme, [...(grouped.get(n.theme) || []), n]);
    else unthemed.push(n);
  });
  model.themes.forEach((theme) => {
    const members = grouped.get(theme) || [];
    const c = centers.get(theme);
    if (!c) return;
    const ring = members.length === 1 ? 0 : base * (model.themes.length === 1 ? 0.24 : 0.085);
    members.forEach((n, i) => {
      const angle = (i / members.length) * 2 * Math.PI + mapPhase(n.id);
      points.set(n.id, { x: c.x + Math.cos(angle) * ring * 1.2, y: c.y + Math.sin(angle) * ring * 0.85 });
    });
  });
  if (unthemed.length) {
    const rim = model.themes.length ? base * 0.47 : base * 0.3;
    unthemed.forEach((n, i) => {
      const angle = (i / unthemed.length) * 2 * Math.PI + 0.4 + mapPhase(n.id) * 0.3;
      points.set(n.id, { x: center.x + Math.cos(angle) * rim * 1.25, y: center.y + Math.sin(angle) * rim * 0.72 });
    });
  }
  points.forEach((p, id) => points.set(id, clamp(p, w, h, 14)));
  return points;
}

export function mapLabelPositions(model: MapModel, w: number, h: number): Map<string, Point> {
  const points = mapLayout(model, w, h);
  const center = { x: w / 2, y: h / 2 };
  const base = Math.min(w, h);
  const out = new Map<string, Point>();
  model.themes.forEach((theme) => {
    const members = model.nodes.filter((n) => n.theme === theme).map((n) => points.get(n.id)).filter((p): p is Point => !!p);
    if (!members.length) return;
    const cx = members.reduce((s, p) => s + p.x, 0) / members.length;
    const cy = members.reduce((s, p) => s + p.y, 0) / members.length;
    let dx = cx - center.x;
    let dy = cy - center.y;
    const len = Math.max(1, Math.hypot(dx, dy));
    dx /= len; dy /= len;
    const push = base * (model.themes.length === 1 ? 0.3 : 0.15) + 10;
    out.set(theme, clamp({ x: cx + dx * push, y: cy + dy * push }, w, h, 10));
  });
  return out;
}

// ── Actions from the view ──────────────────────────────────────────────────

function openWriteWithPrompt(prompt: string): void {
  state.composer.text = "";
  state.composer.placeholder = prompt;
  state.composer.visibility = "private";
  state.composer.attribution = "anonymous";
  state.activeTab = "write";
  window.history.pushState({ tab: "write" }, "", "/write");
  _render();
}

export async function handlePortraitAction(action: string, target: Element): Promise<boolean> {
  const p = state.portrait;
  const el = target as HTMLElement;
  const claimId = el.dataset["claimId"] || "";
  switch (action) {
    case "portrait-enable":
      await enablePortraitAndLoad();
      return true;
    case "portrait-retry":
      await loadPortrait();
      return true;
    case "portrait-mode":
      p.mode = (el.dataset["mode"] as PortraitState["mode"]) || "home";
      p.focusedNodeId = null;
      _render();
      return true;
    case "portrait-decide": {
      const decision = (el.dataset["decision"] || "confirm") as ClaimAction;
      if (!claimId) return true;
      await decidePortrait(claimId, decision);
      return true;
    }
    case "portrait-later":
      if (claimId) p.deferredIds.add(claimId);
      p.notQuiteClaimId = null;
      _render();
      return true;
    case "portrait-not-quite":
      p.notQuiteClaimId = claimId || null;
      _render();
      return true;
    case "portrait-not-quite-close":
      p.notQuiteClaimId = null;
      _render();
      return true;
    case "portrait-undo":
      await undoPortraitDecision();
      return true;
    case "portrait-open-claim":
      p.selectedClaimId = claimId || null;
      p.notQuiteClaimId = null;
      p.claimText = "";
      p.claimTextPurpose = null;
      _render();
      return true;
    case "portrait-close-claim":
      p.selectedClaimId = null;
      p.claimText = "";
      p.claimTextPurpose = null;
      _render();
      return true;
    case "portrait-claim-text-purpose":
      p.claimTextPurpose = (el.dataset["purpose"] as PortraitState["claimTextPurpose"]) || null;
      p.claimText = "";
      _render();
      return true;
    case "portrait-claim-action": {
      const act = (el.dataset["claimAction"] || "") as ClaimAction;
      if (!claimId || !act) return true;
      const needsText = ["correct", "add_context", "qualify", "contextualize"].includes(act) && p.claimTextPurpose;
      const text = needsText ? p.claimText : null;
      if (act === "correct" && !(text || "").trim()) { showToast("Write it in your own words first.", "error"); return true; }
      await performPortraitAction(claimId, act, text);
      return true;
    }
    case "portrait-select-theme": {
      const theme = el.dataset["theme"] || "";
      p.selectedTheme = theme && p.selectedTheme !== theme ? theme : null;
      p.focusedNodeId = null;
      _render();
      return true;
    }
    case "portrait-focus-node": {
      const id = el.dataset["nodeId"] || "";
      if (!id) { p.focusedNodeId = null; _render(); return true; }
      if (p.focusedNodeId === id) { p.selectedClaimId = id; }
      else p.focusedNodeId = id;
      _render();
      return true;
    }
    case "portrait-map-clear":
      p.focusedNodeId = null;
      _render();
      return true;
    case "portrait-filter":
      p.reviewFilter = (el.dataset["filter"] as PortraitState["reviewFilter"]) || "all";
      _render();
      return true;
    case "portrait-grow":
      openWriteWithPrompt(growPrompt());
      return true;
    case "portrait-prompt":
      openWriteWithPrompt(el.dataset["prompt"] || "");
      return true;
    case "portrait-toggle-unreviewed": {
      const enabled = (target as HTMLInputElement).checked;
      try {
        await setPortraitUnreviewedOptIn(enabled);
      } catch {
        showToast(DID_NOT_SAVE, "error");
      }
      _render();
      return true;
    }
    case "portrait-stop":
      await stopPortraitNoticing();
      return true;
    default:
      return false;
  }
}

// Search and free text live in the DOM until used; the morphing renderer
// keeps focused fields, so typing never re-renders the whole surface.
document.addEventListener("input", (event) => {
  const field = (event.target as HTMLElement | null)?.dataset?.["field"];
  if (!field) return;
  if (field === "portrait-search") {
    state.portrait.reviewSearch = (event.target as HTMLInputElement).value;
    _render();
  } else if (field === "portrait-claim-text") {
    state.portrait.claimText = (event.target as HTMLTextAreaElement).value;
  }
});

// Deck swipe on the web: pointer drag on the top card. Right = feels true,
// left = not quite (the gentle follow-up), a short move = nothing. The drag
// is measured from the pointer, never from the moving card, so it cannot
// feed back on itself.
let drag: { id: string; startX: number; startY: number; el: HTMLElement; horizontal: boolean | null } | null = null;

document.addEventListener("pointerdown", (event) => {
  const card = (event.target as HTMLElement | null)?.closest?.(".portrait-deck-card.is-top") as HTMLElement | null;
  if (!card || (event.target as HTMLElement).closest("button, a")) return;
  const id = card.dataset["claimId"] || "";
  if (!id) return;
  drag = { id, startX: event.clientX, startY: event.clientY, el: card, horizontal: null };
  card.classList.add("is-dragging");
});

document.addEventListener("pointermove", (event) => {
  if (!drag) return;
  const dx = event.clientX - drag.startX;
  const dy = event.clientY - drag.startY;
  if (drag.horizontal === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) drag.horizontal = Math.abs(dx) > Math.abs(dy) * 1.15;
  if (drag.horizontal !== true) return;
  drag.el.style.transform = `translateX(${dx}px) rotate(${dx / 26}deg)`;
  const stamp = drag.el.querySelector<HTMLElement>(".portrait-deck-stamp");
  if (stamp) {
    const progress = Math.min(1, Math.max(0, (Math.abs(dx) - 30) / 90));
    stamp.style.opacity = String(progress);
    stamp.textContent = dx > 0 ? "Feels true" : "Not quite";
    stamp.classList.toggle("is-fits", dx > 0);
  }
});

function endDrag(event: PointerEvent): void {
  if (!drag) return;
  const current = drag;
  drag = null;
  current.el.classList.remove("is-dragging");
  const dx = event.clientX - current.startX;
  const dy = event.clientY - current.startY;
  const horizontal = Math.abs(dx) > Math.abs(dy) * 1.15;
  const stamp = current.el.querySelector<HTMLElement>(".portrait-deck-stamp");
  if (stamp) stamp.style.opacity = "0";
  if (horizontal && dx > 110) {
    current.el.style.transform = `translateX(${Math.sign(dx) * 560}px) rotate(${dx / 26}deg)`;
    window.setTimeout(() => { void decidePortrait(current.id, "confirm"); }, 160);
    return;
  }
  current.el.style.transform = "";
  if (horizontal && dx < -110) {
    state.portrait.notQuiteClaimId = current.id;
    _render();
  }
}

document.addEventListener("pointerup", endDrag);
document.addEventListener("pointercancel", endDrag);
